import React, { useState, useEffect } from 'react';
import {
  simulatorAPI,
  departmentAPI,
  shiftAPI,
  machineAPI,
  TestDataRequest,
  TestDataResponse,
  MachineDataOverride,
} from '../services/api';
import { Department, Shift } from '../types/operations';

interface MachineInfo {
  id: number;
  machine_id: string;
  machine_name: string;
  department_id: number;
}

interface MachineRow {
  machine_id: number;
  name: string;
  good_qty: number;
  scrap_qty: number;
  downtime_min: number;
}

interface TestDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerated: (result: TestDataResponse) => void;
}

const getToday = () => new Date().toISOString().split('T')[0];

const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

const generateDefaults = (machines: MachineInfo[]): MachineRow[] => {
  return machines.map((m, i) => {
    const good = randomInt(18000, 32000);
    let scrap: number;
    let downtime: number;

    if (i === 0) {
      scrap = Math.round(good * 0.042);
      downtime = 15;
    } else if (i === 1) {
      scrap = Math.round(good * 0.02);
      downtime = 45;
    } else {
      scrap = Math.round(good * (0.015 + Math.random() * 0.01));
      downtime = randomInt(5, 20);
    }

    return {
      machine_id: m.id,
      name: m.machine_name || m.machine_id,
      good_qty: good,
      scrap_qty: scrap,
      downtime_min: downtime,
    };
  });
};

const TestDataModal: React.FC<TestDataModalProps> = ({ isOpen, onClose, onGenerated }) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [allMachines, setAllMachines] = useState<MachineInfo[]>([]);

  const [selectedDeptId, setSelectedDeptId] = useState<number | ''>('');
  const [selectedShiftId, setSelectedShiftId] = useState<number | ''>('');
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [numViolations, setNumViolations] = useState(2);
  const [machineRows, setMachineRows] = useState<MachineRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSelectedDate(getToday());
    loadInitialData();
  }, [isOpen]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [depts, machines] = await Promise.all([
        departmentAPI.list(),
        machineAPI.list(),
      ]);
      setDepartments(depts.filter(d => d.is_active));
      setAllMachines(machines);

      if (depts.length > 0) {
        const firstDept = depts.filter(d => d.is_active)[0];
        if (firstDept) {
          setSelectedDeptId(firstDept.id);
          await loadDeptData(firstDept.id, machines);
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load departments and machines');
    } finally {
      setLoading(false);
    }
  };

  const loadDeptData = async (deptId: number, machines?: MachineInfo[]) => {
    const machineList = machines || allMachines;
    const deptMachines = machineList.filter(m => m.department_id === deptId);
    setMachineRows(generateDefaults(deptMachines));

    try {
      const deptShifts = await shiftAPI.list(deptId);
      setShifts(deptShifts.filter(s => s.is_active));
      if (deptShifts.length > 0) {
        setSelectedShiftId(deptShifts.filter(s => s.is_active)[0]?.id || '');
      } else {
        setSelectedShiftId('');
      }
    } catch (err) {
      console.error('Failed to load shifts:', err);
      setShifts([]);
      setSelectedShiftId('');
    }
  };

  const handleDeptChange = (deptId: number) => {
    setSelectedDeptId(deptId);
    loadDeptData(deptId);
  };

  const updateMachineRow = (machineId: number, field: keyof MachineRow, value: number) => {
    setMachineRows(prev =>
      prev.map(r => r.machine_id === machineId ? { ...r, [field]: value } : r)
    );
  };

  const handleRandomize = () => {
    const deptMachines = allMachines.filter(m => m.department_id === Number(selectedDeptId));
    setMachineRows(generateDefaults(deptMachines));
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      setGenerating(true);

      const overrides: MachineDataOverride[] = machineRows.map(r => ({
        machine_id: r.machine_id,
        good_qty: r.good_qty,
        scrap_qty: r.scrap_qty,
        downtime_min: r.downtime_min,
      }));

      const config: TestDataRequest = {
        department_id: selectedDeptId ? Number(selectedDeptId) : undefined,
        shift_id: selectedShiftId ? Number(selectedShiftId) : undefined,
        date: selectedDate,
        num_violations: numViolations,
        machine_overrides: overrides,
      };

      const result = await simulatorAPI.generateTestData(config);
      onGenerated(result);
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to generate test data');
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{ background: 'var(--bg-secondary)' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Generate Test Data
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            disabled={generating}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Configuration Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Department
                  </label>
                  <select
                    value={selectedDeptId}
                    onChange={(e) => handleDeptChange(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  >
                    <option value="" disabled>Select...</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Shift
                  </label>
                  <select
                    value={selectedShiftId}
                    onChange={(e) => setSelectedShiftId(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  >
                    <option value="">Auto (first shift)</option>
                    {shifts.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Date
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    KPI Violations
                  </label>
                  <select
                    value={numViolations}
                    onChange={(e) => setNumViolations(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  >
                    <option value={0}>0 - No violations</option>
                    <option value={1}>1 - High scrap alert</option>
                    <option value={2}>2 - Scrap + downtime alerts</option>
                  </select>
                </div>
              </div>

              {/* Machine Data Table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    Machine Data ({machineRows.length} machines)
                  </label>
                  <button
                    onClick={handleRandomize}
                    className="text-xs px-3 py-1 rounded border hover:bg-gray-100 dark:hover:bg-gray-700"
                    style={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  >
                    Randomize
                  </button>
                </div>

                {machineRows.length === 0 ? (
                  <div className="text-center py-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    No machines in this department. Select a different department.
                  </div>
                ) : (
                  <div className="border rounded-md overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <th className="px-3 py-2 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>Machine</th>
                          <th className="px-3 py-2 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>Good Qty</th>
                          <th className="px-3 py-2 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>Scrap Qty</th>
                          <th className="px-3 py-2 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>Scrap %</th>
                          <th className="px-3 py-2 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>Downtime (min)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {machineRows.map((row) => {
                          const total = row.good_qty + row.scrap_qty;
                          const scrapPct = total > 0 ? ((row.scrap_qty / total) * 100).toFixed(1) : '0.0';
                          return (
                            <tr key={row.machine_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td className="px-3 py-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                                {row.name}
                              </td>
                              <td className="px-3 py-1">
                                <input
                                  type="number"
                                  value={row.good_qty}
                                  onChange={(e) => updateMachineRow(row.machine_id, 'good_qty', Math.max(0, parseInt(e.target.value) || 0))}
                                  className="w-full text-right px-2 py-1 border rounded text-sm"
                                  style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                                  min={0}
                                />
                              </td>
                              <td className="px-3 py-1">
                                <input
                                  type="number"
                                  value={row.scrap_qty}
                                  onChange={(e) => updateMachineRow(row.machine_id, 'scrap_qty', Math.max(0, parseInt(e.target.value) || 0))}
                                  className="w-full text-right px-2 py-1 border rounded text-sm"
                                  style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                                  min={0}
                                />
                              </td>
                              <td className="px-3 py-2 text-right" style={{ color: parseFloat(scrapPct) > 3 ? 'rgb(220, 38, 38)' : 'var(--text-secondary)' }}>
                                {scrapPct}%
                              </td>
                              <td className="px-3 py-1">
                                <input
                                  type="number"
                                  value={row.downtime_min}
                                  onChange={(e) => updateMachineRow(row.machine_id, 'downtime_min', Math.max(0, parseInt(e.target.value) || 0))}
                                  className="w-full text-right px-2 py-1 border rounded text-sm"
                                  style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                                  min={0}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="text-xs p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" style={{ color: 'var(--text-secondary)' }}>
                This will create a shift report, machine data records, and KPI violation alerts.
                Existing test data for this department/shift/date will be replaced.
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={generating}
            className="px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50"
            style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={generating || !selectedDeptId || machineRows.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {generating ? 'Generating...' : 'Generate Test Data'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestDataModal;
